import { FormInstance } from 'antd/lib/form';
import { AuthState } from '@packages/@types/authContext';

import {
    FleetsConfigAttributes,
    PriceMatrixAttributes,
    QuoteDenyingConfigAttributes,
} from '../../../../../server/src/models';
import {
    APIForwarder,
    AutoStacking,
    ClientFeatureConfig,
    FEATURES,
    FullEta,
    JSONTransform,
    PreparationTime,
    Webhook,
    ClosingTool,
    E2EFlow,
} from '../../../types/Feature';
import FEATURE_TYPE from '../../../../../server/src/shared/enums/FeatureTypes';
import { Permission } from '../../../types/Auth';
import FeatureService from '../../../services/feature.services';
import ClientController from '../../../controllers/client.controller';
import ClientFeatureConfigController from '../../../controllers/clientFeatureConfig.controller';
import { Client } from '../../../types/Client';
import { FormConfigInterface } from '../types';
import { DriversEtaAttributes } from '../../../../../server/src/models/featureConfigAttributes/DriversEtaAttributes';
import { SmartFleetChoiceAttributes } from '../../../../../server/src/models/featureConfigAttributes/SmartFleetChoiceAttributes';

import {
    extractAPIForwarderFormData,
    extractAutostackingFormData,
    extractClosingToolFormData,
    extractCredentials,
    extractDriversEtaFormData,
    extractFleetsFormData,
    extractFullEtaFormData,
    extractJsonFormData,
    extractPreparationFormData,
    extractPriceMatrixFormData,
    extractQuoteDenyingFormData,
    extractSmartFleetChoiceFormData,
    extractWebhookFormData,
} from './extract';
import { getJsonTransformFeatureToDelete, isFormActionAllowedByPermission } from './helpers';

export type SubmitMethod<T> = (form: FormInstance) => Promise<T>;

export const submitAutostackingFactory = (
    clientId: number,
    data: ClientFeatureConfig<AutoStacking> | undefined,
): SubmitMethod<ClientFeatureConfig<AutoStacking>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<AutoStacking>> => {
    const formData = form.getFieldsValue();
    const autostackingConfig = extractAutostackingFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        Number(clientId),
        { ...autostackingConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.AUTO_STACKING))?.id as number),
        data?.id,
    );
};

export const submitWebhookConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<Webhook> | undefined,
): SubmitMethod<ClientFeatureConfig<Webhook>> => async (form: FormInstance): Promise<ClientFeatureConfig<Webhook>> => {
    const formData = form.getFieldsValue();
    const webhookConfig = extractWebhookFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...webhookConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.WEBHOOK))?.id as number),
        data?.id,
    );
};

export const submitAPIForwarderFactory = (
    clientId: number,
    data: ClientFeatureConfig<APIForwarder> | undefined,
): SubmitMethod<ClientFeatureConfig<APIForwarder>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<APIForwarder>> => {
    const formData = form.getFieldsValue();
    const apiForwarder = extractAPIForwarderFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...apiForwarder },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.API_FORWARDER))?.id as number),
        data?.id,
    );
};

export const submitFleetsConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<FleetsConfigAttributes> | undefined,
): SubmitMethod<ClientFeatureConfig<FleetsConfigAttributes>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<FleetsConfigAttributes>> => {
    const formData = form.getFieldsValue();
    const fleetsConfig = extractFleetsFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...fleetsConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.FLEETS))?.id as number),
        data?.id,
    );
};

export const submitPriceMatrixConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<PriceMatrixAttributes> | undefined,
): SubmitMethod<ClientFeatureConfig<PriceMatrixAttributes>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<PriceMatrixAttributes>> => {
    const formData = form.getFieldsValue();
    const priceMatrix = extractPriceMatrixFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...priceMatrix },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.PRICE_MATRIX))?.id as number),
        data?.id,
    );
};

export const submitJsonTransformConfigFactory = (
    clientId: number,
    user: AuthState['user'] | null,
    originalData: ClientFeatureConfig<JSONTransform>[] | undefined,
    afterCreate?: any,
): SubmitMethod<void> => async (form: FormInstance): Promise<void> => {
    const formData = form.getFieldsValue();
    const jsonTransform = extractJsonFormData(formData, user);
    const jsonTransformToDelete = getJsonTransformFeatureToDelete(formData, originalData || [], user);

    await Promise.all([
        ...((jsonTransformToDelete &&
            jsonTransformToDelete.map(async (id) =>
                ClientFeatureConfigController.deleteClientFeatureConfigById(clientId, id),
            )) ||
            []),
    ]);
    await Promise.all([
        ...((jsonTransform &&
            jsonTransform.map(async (jt) =>
                ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
                    clientId,
                    { ...jt.data },
                    jt.featureId || ((await FeatureService.getFeatureByName(FEATURES.JSON_TRANSFORM))?.id as number),
                    jt.id,
                ),
            )) ||
            []),
    ]);
    afterCreate?.();
};

export const submitClientCredentialsFactory = (clientId: number): SubmitMethod<boolean> => async (
    form: FormInstance,
): Promise<boolean> => {
    const credentials = extractCredentials(form.getFieldsValue());
    if (credentials.apiSecret || credentials.v3email || credentials.v3password) {
        return ClientController.upsertCredentials(clientId, credentials);
    }
    return false;
};

export const submitDriversEtaConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<DriversEtaAttributes> | undefined,
): SubmitMethod<ClientFeatureConfig<DriversEtaAttributes>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<DriversEtaAttributes>> => {
    const formData = form.getFieldsValue();
    const driversEtaConfig = extractDriversEtaFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...driversEtaConfig },
        (await FeatureService.getFeatureByName(FEATURE_TYPE.DRIVERS_ETA))?.id as number,
        data?.id,
    );
};

export const submitSmartFleetChoiceConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<SmartFleetChoiceAttributes> | undefined,
): SubmitMethod<ClientFeatureConfig<SmartFleetChoiceAttributes>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<SmartFleetChoiceAttributes>> => {
    const formData = form.getFieldsValue();
    const sfcConfig = extractSmartFleetChoiceFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...sfcConfig },
        (await FeatureService.getFeatureByName(FEATURE_TYPE.SMART_FLEET_CHOICE))?.id as number,
        data?.id,
    );
};

export const submitClientFactory = (
    clientId: number | undefined,
    formConfig: FormConfigInterface,
    user: AuthState['user'] | null,
    afterCreate: any,
): ((form: FormInstance) => Promise<Client | undefined>) => async (form: FormInstance): Promise<Client | undefined> => {
    const formData = form.getFieldsValue();
    let client;
    if (formConfig.isEditMode && clientId) {
        // Update client (Beforehand, we need to check the proper permission to perform the action)...
        if (isFormActionAllowedByPermission(user, Permission.CLIENT_EDIT)) {
            client = await ClientController.updateClientById(clientId, {
                name: formData.name,
                apiClientId: formData.apiClientId,
                stuartClientId: formData.stuartClientId,
                storeId: [
                    formData.clientExternalId.retailerId || '',
                    formData.clientExternalId.storeId,
                    formData.clientExternalId.retailerCode,
                ].join(';'),
                linkedUserIds: formData.linkedUserIds,
                jetToken: formData.jetToken || null,
            });
        }
    } else {
        client = await ClientController.createClient({
            name: formData.name,
            apiClientId: formData.apiClientId,
            stuartClientId: formData.stuartClientId,
            storeId: formData.storeId,
            linkedUserIds: formData.linkedUserIds,
            jetToken: formData.jetToken || null,
        });

        if (formConfig.isAddMode) {
            afterCreate(client.id);
        }
    }
    return client;
};

export const submitQuoteDenyingConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<QuoteDenyingConfigAttributes> | undefined,
): SubmitMethod<ClientFeatureConfig<QuoteDenyingConfigAttributes>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<QuoteDenyingConfigAttributes>> => {
    const formData = form.getFieldsValue();
    const quoteDenyingConfig = extractQuoteDenyingFormData(formData);

    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...quoteDenyingConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURE_TYPE.QUOTE_DENYING))?.id as number),
        data?.id,
    );
};

export const submitFullEtaConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<FullEta> | undefined,
): SubmitMethod<ClientFeatureConfig<FullEta>> => async (form: FormInstance): Promise<ClientFeatureConfig<FullEta>> => {
    const formData = form.getFieldsValue();
    const fullEtaConfig = extractFullEtaFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...fullEtaConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.FULL_ETA))?.id as number),
        data?.id,
    );
};

export const submitPreparationTimeConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<PreparationTime> | undefined,
): SubmitMethod<ClientFeatureConfig<PreparationTime>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<PreparationTime>> => {
    const formData = form.getFieldsValue();
    const preparationConfig = extractPreparationFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...preparationConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.PREPARATION_TIME))?.id as number),
        data?.id,
    );
};

export const submitClosingToolConfigFactory = (
    clientId: number,
    data: ClientFeatureConfig<ClosingTool> | undefined,
): SubmitMethod<ClientFeatureConfig<ClosingTool>> => async (
    form: FormInstance,
): Promise<ClientFeatureConfig<ClosingTool>> => {
    const formData = form.getFieldsValue();
    const closingConfig = extractClosingToolFormData(formData);
    return ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId(
        clientId,
        { ...closingConfig },
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.CLOSING_TOOL))?.id as number),
        data?.id,
    );
};

export const submitE2EFlowConfigFactory = async (
    clientId: number,
    data: ClientFeatureConfig<E2EFlow>,
): Promise<ClientFeatureConfig<E2EFlow>> => {
    const response = await ClientFeatureConfigController.upsertClientFeatureConfigByFeatureConfigId<E2EFlow>(
        clientId,
        data.config,
        data?.featureId || ((await FeatureService.getFeatureByName(FEATURES.E2E_FLOW))?.id as number),
        data?.id,
    );
    return response;
};
